import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faEye, faArrowLeft, faShareAlt } from '@fortawesome/free-solid-svg-icons';
import BlogPostContent from '../components/BlogPostPage/BlogPostContent';
import RelatedPosts from '../components/BlogPostPage/RelatedPosts';
import BlogPostShare from '../components/BlogPostPage/BlogPostShare';
import WebPreloader from '../../core/components/Common/WebPreloader';
import SEOHead from '../../core/components/Common/SEOHead';
import BlogService from '../services/BlogService';
import emojiImage from '../../images/BlogPage/MrSeaMiami_Emoji_LeonardoGalarza.png';
import '../styles/BlogPostPage.css';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [showPreloader, setShowPreloader] = useState(true);
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setShowPreloader(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await BlogService.fetchPostBySlug(slug);
      console.log('🔍 [BlogPostPage] Response from service:', data);
      
      // Nueva estructura: data.post y data.related_posts
      if (data && data.post && data.post.id) {
        setPost(data.post);
        setRelatedPosts(data.related_posts || []);
        console.log('🔍 [BlogPostPage] Set post and related posts:', data.post.title, data.related_posts?.length || 0);
      } else if (data && data.id) {
        // Fallback: si data es el post directamente (estructura anterior)
        setPost(data);
        setRelatedPosts([]);
        console.log('🔍 [BlogPostPage] Fallback: post directly, no related posts');
      } else {
        // Fallback
        setPost(null);
        setRelatedPosts([]);
        setError('Invalid post data received');
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      setError('Error loading post');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="blog-post-page">
        {showPreloader && <WebPreloader />}
        <div className="blog-post-page__loading">
          <div className="container">
            <div className="loading-spinner">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-page">
        {showPreloader && <WebPreloader />}
        <div className="blog-post-page__error">
          <div className="container">
            <div className="error-content">
              <h2>Oops! Something went wrong</h2>
              <p>{error || 'Post not found'}</p>
              <Link to="/blog" className="button">
                <FontAwesomeIcon icon={faArrowLeft} /> Back to Blog
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page">
      <SEOHead postSlug={slug} />
      {showPreloader && <WebPreloader />}
      
      {/* Hero Section */}
      <section className="blog-post-hero">
        <div className="blog-post-hero__background">
          <div className="blog-post-hero__overlay"></div>
        </div>
        
        <div className="blog-post-hero__content">
          <div className="container">
            <div className="blog-post-hero__right-content">
              <Link to="/blog" className="blog-post-hero__back-link">
                <FontAwesomeIcon icon={faArrowLeft} /> Back to Blog
              </Link>
              <h1 className="blog-post-hero__title">{post.title}</h1>
            </div>
            <div className="blog-post-hero__left-content">
              <div className="blog-post-hero__emoji">
                <img src={emojiImage} alt="Mr. Sea Emoji" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="blog-post-page__content">
        <div className="container">
          <div className="blog-post-layout">
            <main className="blog-post-main">
              <BlogPostContent post={post} />
            </main>
            
            <aside className="blog-post-sidebar">
              <RelatedPosts posts={relatedPosts} />
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostPage; 